<template>
    <DsCard
        class="card"
        elevation="z0"
    >
        <DsCardHeader
            class="card-header"
        >
            <h2>
                {{ $t('funnel.canvas.reporting.email.performance.title') }}
            </h2>
            <DsIconButton
                class="collapse-button"
                data-qa="toggle-collapse"
                :name="collapsed ? 'chevron-down' : 'chevron-up'"
                @click="toggleCollapsible"
            />
        </DsCardHeader>

        <DsCardBody class="card-body">
            <DsCollapsible
                data-qa="collapsible"
                :is-open="collapsed"
            >
                <div class="automation-reporting-list-group">
                    <DsListItem
                        as="a"
                        data-qa="email-sent"
                        :title="$t('funnel.canvas.reporting.email.performance.sent.email')"
                        @click="openReport('CampaignEmailSentReport')"
                    >
                        <template #leading>
                            <DsIcon name="send" />
                        </template>

                        <template #trailing>
                            <p
                                data-qa="sent-count"
                                class="email-count-number"
                            >
                                {{ emailStats.sentCount }}
                            </p>
                        </template>
                    </DsListItem>
                    <DsListItem
                        as="a"
                        data-qa="delivered-email"
                        :title="$t('funnel.canvas.reporting.email.performance.delivered.email')"
                        @click="openReport('CampaignEmailDeliveredReport')"
                    >
                        <template #leading>
                            <DsIcon name="inbox" />
                        </template>

                        <template #trailing>
                            <p
                                data-qa="delivered-count"
                                class="email-count-number"
                            >
                                {{ emailStats.deliveredCount }}
                            </p>
                        </template>
                    </DsListItem>
                    <DsListItem
                        as="a"
                        data-qa="open-email"
                        :title="$t('funnel.canvas.reporting.email.performance.opens.email')"
                        :short-description="openEmailsPercentage"
                        @click="openReport('CampaignEmailOpenReport')"
                    >
                        <template #leading>
                            <DsIcon name="email-open" />
                        </template>

                        <template #trailing>
                            <p
                                data-qa="opened-count"
                                class="email-count-number"
                            >
                                {{ emailStats.openedCount }}
                            </p>
                        </template>
                    </DsListItem>
                    <DsListItem
                        as="a"
                        data-qa="click-email"
                        :title="$t('funnel.canvas.reporting.email.performance.clicks')"
                        :short-description="emailClickPercentage"
                        @click="openReport('CampaignEmailClickReport')"
                    >
                        <template #leading>
                            <DsIcon name="click" />
                        </template>

                        <template #trailing>
                            <p
                                data-qa="clicked-count"
                                class="email-count-number"
                            >
                                {{ emailStats.clickedCount }}
                            </p>
                        </template>
                    </DsListItem>
                    <DsListItem
                        as="a"
                        data-qa="unopen-email"
                        :title="$t('funnel.canvas.reporting.email.performance.unopens')"
                        :short-description="unopenPercentage"
                        @click="openReport('CampaignEmailUnopenReport')"
                    >
                        <template #leading>
                            <DsIcon name="email" />
                        </template>

                        <template #trailing>
                            <p
                                data-qa="unopened-count"
                                class="email-count-number"
                            >
                                {{ emailStats.unopenedCount }}
                            </p>
                        </template>
                    </DsListItem>
                    <DsListItem
                        as="a"
                        data-qa="opt-out-email"
                        :title="$t('funnel.canvas.reporting.email.performance.opt.outs')"
                        :short-description="optOutsPercentage"
                        @click="openReport('CampaignEmailOptOutReport')"
                    >
                        <template #leading>
                            <DsIcon name="user-x" />
                        </template>

                        <template #trailing>
                            <p
                                data-qa="opt-out-count"
                                class="email-count-number"
                            >
                                {{ emailStats.optOutCount }}
                            </p>
                        </template>
                    </DsListItem>
                    <DsListItem
                        as="a"
                        data-qa="bounce-email"
                        :title="$t('funnel.canvas.reporting.email.performance.bounces')"
                        :short-description="bouncesPercentage"
                        @click="openReport('CampaignEmailBounceReport')"
                    >
                        <template #leading>
                            <img src="/resources/funnel/images/svg/reporting/email-x.svg" />
                        </template>

                        <template #trailing>
                            <p
                                data-qa="bounced-count"
                                class="email-count-number"
                            >
                                {{ emailStats.bounceCount }}
                            </p>
                        </template>
                    </DsListItem>
                    <DsListItem
                        as="a"
                        data-qa="complaint-email"
                        :title="$t('funnel.canvas.reporting.email.performance.complaints')"
                        :short-description="complainsPercentage"
                        @click="openReport('CampaignEmailComplaintReport')"
                    >
                        <template #leading>
                            <DsIcon name="alert-octagon" />
                        </template>

                        <template #trailing>
                            <p
                                data-qa="complaint-count"
                                class="email-count-number"
                            >
                                {{ emailStats.complaintCount }}
                            </p>
                        </template>
                    </DsListItem>
                </div>
            </DsCollapsible>
        </DsCardBody>
        <DsCardFooter
            class="email-performance-card-footer"
        >
            <div class="button-section">
                <DsTextButton
                    class="card-button"
                    @click="$emit('open-sequence-email-report')"
                >
                    <template #default>
                        {{ $t('funnel.canvas.reporting.email.performance.button') }}
                    </template>
                    <template #trailing>
                        <DsIcon name="arrow-long-right" />
                    </template>
                </DsTextButton>
            </div>
        </DsCardFooter>
    </DsCard>
</template>
<script>
export default {
    props: {
        emailStats: {
            type: Object,
            required: true,
        },
    },
    emits: ['open-email-performance-report', 'open-sequence-email-report'],

    data() {
        return {
            collapsed: true,
            percentageLabel: 'funnel.canvas.reporting.email.performance.percentage.description',
        };
    },

    computed: {
        openEmailsPercentage() {
            return this.$t(this.percentageLabel, { percentage: this.emailStats.percentOpened });
        },
        emailClickPercentage() {
            return this.$t(this.percentageLabel, { percentage: this.emailStats.percentClicked });
        },
        unopenPercentage() {
            return this.$t(this.percentageLabel, { percentage: this.emailStats.percentUnopened });
        },

        optOutsPercentage() {
            return this.$t(this.percentageLabel, { percentage: this.emailStats.percentOptedOut });
        },

        bouncesPercentage() {
            return this.$t(this.percentageLabel, { percentage: this.emailStats.percentBounced });
        },

        complainsPercentage() {
            return this.$t(this.percentageLabel, { percentage: this.emailStats.percentComplained });
        },
    },

    methods: {
        toggleCollapsible() {
            this.collapsed = !this.collapsed;
        },

        openReport(type) {
            this.$emit('open-email-performance-report', type);
        },
    },
};
</script>
<style lang="scss" scoped>
    .card {
        width: px-to-rem(310);
        max-height: px-to-rem(620);
        min-height: px-to-rem(70);
        align-self: flex-end;
        z-index: 50;
        position: absolute;
        right: px-to-rem(20);
        top: px-to-rem(20);

        &:active {
            box-shadow: $elevation-z0;
        }
    }

    .card-body {
        line-height: $line-height-sm;
        flex-grow: 1;
        padding: 0;
    }

    h2 {
        font-size: $font-size-sm;
        font-weight: $font-weight-semibold;
    }

    .button-section {
        display: flex;
        width: 100%;
        justify-content: center;
        margin-top: auto;
    }

    .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-top: $spacing-100;
    }

    .collapse-button:hover {
        background-color: transparent;
    }

    .card-button {
        color: $color-blue;
        font-size: $font-size-sm;
        font-weight: $font-weight-semibold;
        line-height: $line-height-xs;
        padding-left: 0;
        padding-right: 0;
        &:hover {
            background-color: transparent;
        }
    }

    .email-count-number {
        font-size: $font-size-xl;
        font-weight: $font-weight-bold;
        color: black;
    }

    .collapsible .collapsible-inner {
        padding: 0;
    }
    .ds-card-footer.email-performance-card-footer {
        padding: $spacing-050;
    }
</style>
<style lang="scss">
    .automation-reporting-list-group .list-item {
        padding: $spacing-050 $spacing-050 0;
    }

    .automation-reporting-list-group .list-item .list-item-info  {
        padding-bottom: $spacing-075;
    }
</style>
